import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getUserVerified } from '../../api/tvs/userVerifications';
import { getUserVeriffVerificationStatus } from '../../api/veriff/veriffVerification';
import { VerificationStatus } from '../../@types/VerificationStatus';
import { VeriffStatus } from '../../api/veriff/model/UserVeriffVerificationStatus';
import { useVeriffSession } from '../../utils/veriffSessionContext';
import VerificationStatusCheck from '../../components/verificationStatusCheck/VerificationStatusCheck';
import { VerificationMethod } from '../../@types/VerificationMethod';
import { trackEvent } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';

const POLLING_INTERVAL_MS = 1000;
const PROGRESS_INCREMENT = 20;
const MAX_PROGRESS = 95; // Maximum progress while checking
const COMPLETION_DELAY_MS = 1000;
const MAX_VERIFICATION_TIME_MS = 15000; // 15 seconds max verification time
const CLOSE_BUTTON_DELAY_MS = 5000; // 5 seconds before showing close button
const PROGRESS_COMPLETE_TIME_MS = 14000; // 14 seconds before setting progress to 100%

interface VeriffStatusCheckPageProps {
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const VeriffStatusCheckPage: React.FC<VeriffStatusCheckPageProps> = ({ onVerificationComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isChecking, setIsChecking] = useState(true);
  const [, setSearchParams] = useSearchParams();
  const { sessionId } = useVeriffSession();
  const [shouldPollVeriff, setShouldPollVeriff] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [showFullProgress, setShowFullProgress] = useState(false);
  const { clientId } = useClientConfig();

  const handleVerificationSuccess = useCallback(() => {
    setShowFullProgress(true);
    setProgress(100);
    setTimeout(() => {
      setIsChecking(false);
      trackEvent('is24.de.sicherheit.verifizierung.veriff', 'verification_successful', 'user', clientId, 'veriff');
      setSearchParams({ tvs_state: 'verification_success' });
      onVerificationComplete?.(VerificationStatus.SUCCESS);
    }, COMPLETION_DELAY_MS);
  }, [onVerificationComplete, setSearchParams]);

  const handleVerificationFailure = useCallback(() => {
    setShowFullProgress(true);
    setProgress(100);
    setTimeout(() => {
      setIsChecking(false);
      setSearchParams({ tvs_state: 'verification_failure_veriff' });
      onVerificationComplete?.(VerificationStatus.FAILED);
    }, COMPLETION_DELAY_MS);
  }, [onVerificationComplete, setSearchParams]);

  const handleClose = useCallback(() => {
    setIsChecking(false);
    setSearchParams({ tvs_state: 'verification_failure_veriff' });
    onVerificationComplete?.(VerificationStatus.FAILED);
  }, [onVerificationComplete, setSearchParams]);

  const checkVeriffStatus = useCallback(async () => {
    try {
      const veriffResponse = await getUserVeriffVerificationStatus(sessionId || undefined);

      if (veriffResponse.status === VeriffStatus.VERIFIED && veriffResponse.verified) {
        handleVerificationSuccess();
      } else if (
        veriffResponse.status === VeriffStatus.EXPIRED ||
        veriffResponse.status === VeriffStatus.DECLINED ||
        veriffResponse.status === null ||
        veriffResponse.status === undefined
      ) {
        handleVerificationFailure();
      }
    } catch (error) {
      console.error('Error checking Veriff status:', error);
    }
  }, [sessionId, handleVerificationSuccess, handleVerificationFailure]);

  const initialVerificationCheck = useCallback(async () => {
    try {
      const response = await getUserVerified();
      if (response.isVerified) {
        handleVerificationSuccess();
      } else {
        setShouldPollVeriff(true);
      }
    } catch (error) {
      console.error('Error checking user verification:', error);
      setShouldPollVeriff(true);
    }
  }, [handleVerificationSuccess]);

  const updateProgress = useCallback(() => {
    setProgress((prevProgress) => {
      return Math.min(prevProgress + PROGRESS_INCREMENT, MAX_PROGRESS);
    });
  }, []);

  useEffect(() => {
    let verificationTimer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;
    let maxVerificationTimer: NodeJS.Timeout;
    let closeButtonTimer: NodeJS.Timeout;
    let progressCompleteTimer: NodeJS.Timeout;

    const cleanup = () => {
      clearInterval(progressTimer);
      clearInterval(verificationTimer);
      clearTimeout(maxVerificationTimer);
      clearTimeout(closeButtonTimer);
      clearTimeout(progressCompleteTimer);
    };

    if (isChecking) {
      // Set maximum verification time limit
      maxVerificationTimer = setTimeout(() => {
        if (isChecking) {
          handleVerificationFailure();
        }
      }, MAX_VERIFICATION_TIME_MS);

      // Set timer to show close button after delay
      closeButtonTimer = setTimeout(() => {
        setShowCloseButton(true);
      }, CLOSE_BUTTON_DELAY_MS);

      // Set timer to make progress 100% after 14 seconds
      progressCompleteTimer = setTimeout(() => {
        if (isChecking) {
          console.log('Forcing progress to 100% at 14 seconds');
          // Force progress to exactly 100%
          setShowFullProgress(true);
          setProgress(100);
        }
      }, PROGRESS_COMPLETE_TIME_MS);

      if (!shouldPollVeriff) {
        initialVerificationCheck();
      } else {
        verificationTimer = setInterval(checkVeriffStatus, POLLING_INTERVAL_MS);
        progressTimer = setInterval(updateProgress, POLLING_INTERVAL_MS);
      }

      return () => {
        cleanup();
      };
    }
  }, [
    isChecking,
    shouldPollVeriff,
    initialVerificationCheck,
    checkVeriffStatus,
    updateProgress,
    handleVerificationFailure,
  ]);

  if (!isChecking) return null;

  // Calculate the final progress value
  const finalProgress = showFullProgress ? 100 : progress;

  return (
    <div data-testid={`verification-status-${shouldPollVeriff ? 'polling' : 'initial'}`}>
      <VerificationStatusCheck
        progress={finalProgress}
        verificationMethod={VerificationMethod.Veriff}
        showCloseButton={showCloseButton}
        onClose={handleClose}
      />
    </div>
  );
};

export default VeriffStatusCheckPage;
